import { useGetQuoteQuery } from './studioApi'
import type { ActiveJob, Quote } from './studioStartTypes'
import { addCredits } from './lib/money'

export interface TranslationQuoteView {
  pendingUnits?: number
  totalUnits?: number
  /** Полная цена мягкого режима: pending_cost + images_cost. */
  pendingTotalCost?: string
  /** Полная цена разрушительного режима: overwrite_cost + images_cost. */
  overwriteTotalCost?: string
  textCost?: string
  /** Сырая цена разрушительного режима БЕЗ картинок — симметрично `textCost`.
   *  Нужна снятому тумблеру: `overwriteTotalCost` включает картинки. */
  overwriteCost?: string
  imagesCost?: string
  imagesCount?: number
  balance?: string
  activeJob: ActiveJob | null
  /** §6.2: база правилась после перевода — та же ручка, другая подпись кнопки. */
  hasOutdated: boolean
  isLoading: boolean
  refetch: () => void
}

const sum = (a?: string, b?: string): string | undefined =>
  a === undefined || b === undefined ? undefined : addCredits(a, b)

/**
 * Куски сметы, из которых собирается вид. Типы ПРОИЗВОДНЫЕ от `Quote`, а не
 * структурная копия его полей: переименование поля в контракте обязано ломать
 * сборку здесь, а не молча превращать цену в `undefined` и печатать ноль.
 * `Partial` — потому что чтение защитное: неполный ответ не роняет экран
 * настроек (та же манера, что у `useImageQuote`).
 */
interface TranslationData {
  data?: Partial<Pick<Quote, 'balance' | 'active_job'>>
  translation?: Partial<Quote['translation']>
  isLoading: boolean
  refetch: () => void
}

function buildTranslationQuoteView({
  data,
  translation,
  isLoading,
  refetch,
}: TranslationData): TranslationQuoteView {
  // Одна проверка на пустоту вместо `?.` на каждом поле: их набралось столько,
  // что функция упиралась в лимит сложности.
  const t = translation ?? {}
  return {
    pendingUnits: t.pending_units,
    totalUnits: t.total_units,
    pendingTotalCost: sum(t.pending_cost, t.images_cost),
    overwriteTotalCost: sum(t.overwrite_cost, t.images_cost),
    textCost: t.pending_cost,
    overwriteCost: t.overwrite_cost,
    imagesCost: t.images_cost,
    imagesCount: t.images_count,
    balance: data?.balance,
    activeJob: data?.active_job ?? null,
    hasOutdated: t.has_outdated === true,
    isLoading,
    refetch,
  }
}

/**
 * Переводческая часть сметы. Чтение защитное по всей цепочке — как у
 * `useImageQuote`: неполный ответ не роняет экран настроек.
 *
 * Цену кнопки считаем СУММОЙ (§1.4): ровно её проверяет предчек баланса на
 * бэке, и `400 not_enough_ai_credits` приходит с `required` = сумме.
 *
 * `refetchOnMountOrArgChange`: вкладка обязана отражать момент открытия, а не
 * смету, закешированную до правки половины курса.
 */
export const useTranslationQuote = (courseId: number, skip: boolean): TranslationQuoteView => {
  const { data, isFetching, refetch } = useGetQuoteQuery(courseId, {
    skip,
    refetchOnMountOrArgChange: true,
  })
  return buildTranslationQuoteView({
    data,
    translation: data?.translation,
    isLoading: !skip && isFetching,
    refetch: () => void refetch(),
  })
}
